/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Alert } from 'antd';
import moment from 'moment';
import { describeBlackoutForMoment, getBlackoutConfig, toBlackoutDayString } from '../services/blackoutdates.service';

/**
 * Card #254 round 3 — "If you have a blacked out date the schedules page on the
 * admin side should have a header that says Date has been blocked by an admin
 * or something to that effect." (Joel, 2026-08-19)
 *
 * One banner shared by every screen that shows a schedule for day(s) the user
 * picked. It reads the calendar the app already caches on `window.BLACKOUTDATES`
 * (primed at bootstrap by config/setup.js) through the already-exported
 * `describeBlackoutForMoment`, so there is no fetch here and no second copy of
 * the predicate — the same function the order form's date fields use.
 *
 * Informational only. It deliberately does NOT disable anything: an admin must
 * still be able to VIEW a blacked-out day, because orders placed before the
 * blackout can sit on it (round 1 classified these pickers as "view filters
 * that must not be blacked out").
 *
 * Renders nothing when none of the days handed to it are blacked out, so
 * callers can mount it unconditionally.
 */

const styles = {
  alert: `
    width: 100%;
    margin-bottom: 10px;
  `,
  note: `
    margin-top: 4px;
  `,
};

/**
 * One human sentence for one blacked-out day.
 *
 * Same phrasing as the order form's admin override confirm
 * (order.component.jsx:2106 — "<date> is blacked out: <label>") so the two
 * surfaces speak one vocabulary.
 *
 * `describeBlackout` labels a blocked WEEKDAY "Sundays are not available",
 * which is ungrammatical inside "... is blacked out: {label}", so that kind
 * gets its own sentence.
 */
const lineFor = (day, hit) => {
  if (hit.kind === 'weekday') {
    return `${hit.label} for scheduling.`;
  }
  return `${moment(day).format('ddd, MMM D, YYYY')} is blacked out: ${hit.label}`;
};

export const BlackoutBanner = props => {
  const { dates = [], style } = props;

  // Read the calendar once so every day in `dates` is described against the
  // same snapshot.
  const cfg = getBlackoutConfig();

  const hits = dates
    .map(d => ({ day: toBlackoutDayString(d), hit: describeBlackoutForMoment(d, cfg) }))
    .filter(h => !!h.hit);

  if (!hits.length) { return null; }

  const many = hits.length > 1;

  return (
    <Alert
      // This screen has a real print pipeline; a warning banner on a printed
      // manifest is noise.
      className="no-print"
      css={css(styles.alert)}
      style={style}
      type="warning"
      showIcon
      // Not closable on purpose: it describes the day currently selected, not a
      // notification. Dismissing it and then changing the date would leave the
      // screen claiming nothing is blocked when something is.
      message={many
        ? `${hits.length} days shown here are blacked out`
        : lineFor(hits[0].day, hits[0].hit)}
      description={many
        ? (
          <span>
            {hits.map(h => <div key={h.day}>{lineFor(h.day, h.hit)}</div>)}
            <div css={css(styles.note)}>These dates have been blocked by an admin. Blacked-out days are not offered to customers.</div>
          </span>
        )
        : 'This date has been blocked by an admin. Blacked-out days are not offered to customers.'}
    />
  );
};

export default BlackoutBanner;
